import { useState, useEffect } from "react";
import { Form, Upload, message } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { ProfileContext } from "Common/hooks/context/ProfileContext";

const useMentorProfile = () => {
  
  const token = localStorage.getItem("token");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [skills, setSkills] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const navigate = useNavigate();
  const { markProfileComplete, fetchProfileStatus} = useContext(ProfileContext);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get skills
        const skillsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/MentorSkills/GetAllSkills`,
          {
            headers: {
              accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSkills(skillsResponse.data);

        // Get languages
        const languagesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/Mentor/SpokenLanguagesList`,
          {
            headers: {
              accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setLanguages(languagesResponse.data);

        // Get profile data
        const profileResponse = await axios.get(`${process.env.REACT_APP_API_URL}/Mentor/Profile`, {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        });
        if (profileResponse.data.profileImage) {
          setImageUrl(`${process.env.REACT_APP_URL}${profileResponse.data.profileImage}`);
        }

        // Set form values
        form.setFieldsValue({
          firstName: profileResponse.data.firstName,
          lastName: profileResponse.data.lastName,
          email: profileResponse.data.email,
          phoneNo: profileResponse.data.phoneNo,
          country: profileResponse.data.country,
          state: profileResponse.data.state,
          city: profileResponse.data.city,
          mentorSkills: (profileResponse.data.mentorSkills || []).map((skill) => skill.name),
          softSkills: profileResponse.data.softSkills || [],
          certifications: profileResponse.data.certifications || [],
          languagesSpoken: (profileResponse.data.languagesSpoken || []).map((lang) => lang.id),
          yearsOfExperience: profileResponse.data.yearsOfExperience,
          mentorshipExperienceLevel: profileResponse.data.mentorshipExperienceLevel,
          trainingModes: profileResponse.data.trainingModes || [],
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [form, token]);

    const onFinish = async (values) => {
    try {
      setSubmitting(true);
      // Prepare form data
      const formData = new FormData();
      formData.append("FirstName", values.firstName);
      formData.append("LastName", values.lastName);
      formData.append("PhoneNo", values.phoneNo);
      formData.append("YearsOfExperience", values.yearsOfExperience);
      formData.append("MentorshipExperienceLevel", values.mentorshipExperienceLevel);
      formData.append("Country", values.country);
      formData.append("State", values.state);
      formData.append("City", values.city);

      if (values.mentorSkills) {
        values.mentorSkills.forEach((skillName) => {
          formData.append("MentorSkills", skillName);
        });
      }

      // Add soft skills
      if (values.softSkills) {
        values.softSkills.forEach((skill) => {
          formData.append("SoftSkills", skill);
        });
      }

      // Add training modes
      if (values.trainingModes) {
        values.trainingModes.forEach((mode) => {
          formData.append("TrainingModes", mode);
        });
      }

      // Add languages
      if (values.languagesSpoken) {
        values.languagesSpoken.forEach((lang) => {
          formData.append("LanguagesSpoken", lang);
        });
      }

      // Add certifications
      if (values.certifications) {
        values.certifications.forEach((cert) => {
          formData.append("Certifications", cert);
        });
      }

      // Add image if uploaded
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("ImageFile", fileList[0].originFileObj);
      }

      // Make API call
      await axios.put(`${process.env.REACT_APP_API_URL}/Mentor/Profile`, formData, {
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("Profile updated successfully!");
      markProfileComplete();
      setTimeout(() => {
  fetchProfileStatus();
}, 1000);
        navigate("/mentor-dashboard");
    } catch (error) {
      console.error("Error updating profile:", error);

      // Display detailed error message
      let errorMessage = "Failed to update profile";

      if (error.response) {
        // The request was made and the server responded with a status code
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);

        errorMessage =
          error.response.data.message ||
          error.response.data.title ||
          JSON.stringify(error.response.data);
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Request:", error.request);
        errorMessage = "No response received from server";
      } else {
        // Something happened in setting up the request
        console.error("Error message:", error.message);
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    const isLt2M = file.size / 1024 / 1024 < 2;

    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return Upload.LIST_IGNORE;
    }

    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleImageChange = (info) => {
    let newFileList = [...info.fileList].slice(-1);

    newFileList = newFileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(newFileList);

    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => setImageUrl(e.target.result);
      reader.readAsDataURL(newFileList[0].originFileObj);
    }
  };

  return {
    form,
    fileList,
    handleImageChange,
    beforeUpload,
    onFinish,
    imageUrl,
    languages,
    skills,
    submitting,
    loading,
  };
};

export default useMentorProfile;
